/**
 * Floating contact (render.php). Above 768: hidden at the top of the page; once the page is scrolled past half a
 * screen the discs slide in from the right one after another, bottom first, and scrolling back to the top hides them
 * again (owner 2026-10-22; timing: MOTION.reveal). Without motion (?still, reduced motion, no GSAP) they simply stay
 * visible. At ≤ 768 the glass bar is always there (CSS only, floating-contact.css).
 *
 * HubSpot chat (button[data-px-hubspot-chat], as on the old hola.tech): HubSpot's script loads on the first click only,
 * then the chat opens; HubSpot's own chat bubble is removed again when the chat is closed. Docs:
 * https://developers.hubspot.com/docs/api/conversation/chat-widget-sdk
 */
import { MOTION, motionLib } from '../../assets/js/motion';

const SHOW_AFTER = 0.5; // part of the screen height scrolled before the buttons show
const FROM_RIGHT = 40; // px each disc slides in from
const bar = window.matchMedia('(width <= 768px)');

interface HubSpotWidget {
	load: (opts?: { widgetOpen?: boolean }) => void;
	open: () => void;
	remove: () => void;
	status: () => { loaded: boolean };
}
declare global {
	interface Window {
		hsConversationsSettings?: { loadImmediately?: boolean };
		hsConversationsOnReady?: Array<() => void>;
		HubSpotConversations?: { widget: HubSpotWidget; on: (event: string, fn: () => void) => void };
	}
}

export function initFloatingContact(): void {
	const el = document.querySelector<HTMLElement>('[data-px-floating-contact]');
	if (!el) return;

	initHubSpotChat(el);
	initLabel(el);

	const lib = motionLib();
	if (!lib || !document.documentElement.classList.contains('px-motion')) {
		el.setAttribute('data-px-floating-contact-shown', '');
		return;
	}
	const { gsap } = lib;
	const { duration, stagger, ease } = MOTION.reveal;
	const items = [...el.querySelectorAll<HTMLElement>('li')].reverse(); // bottom first
	let shown = false;
	const update = () => {
		const next = window.scrollY > window.innerHeight * SHOW_AFTER;
		if (next === shown) return;
		shown = next;
		gsap.killTweensOf(items);
		gsap.set(items, { clearProps: 'opacity,visibility,transform' });
		// the bar: always shown (CSS)
		if (bar.matches) {
			el.toggleAttribute('data-px-floating-contact-shown', shown);
			return;
		}
		if (shown) {
			el.setAttribute('data-px-floating-contact-shown', '');
			gsap.fromTo(items, { autoAlpha: 0, x: FROM_RIGHT }, { autoAlpha: 1, x: 0, duration, stagger, ease, clearProps: 'opacity,visibility,transform' });
		} else {
			gsap.to(items, {
				autoAlpha: 0, x: FROM_RIGHT, duration: duration / 2, stagger: stagger / 2, ease: 'power2.in',
				onComplete: () => {
					el.removeAttribute('data-px-floating-contact-shown');
					gsap.set(items, { clearProps: 'opacity,visibility,transform' });
				},
			});
		}
	};
	update();
	window.addEventListener('scroll', update, { passive: true });
}

/**
 * Above 768 with a mouse (or keyboard focus): one label pill left of the buttons. It glides to the button hovered or
 * focused (--y) and takes its text's width (--w), on the CSS spring; it pops in at its first button and goes when the
 * pointer leaves the buttons (or focus leaves them).
 */
function initLabel(el: HTMLElement): void {
	const label = el.querySelector<HTMLElement>('.px-floating-contact__label');
	const text = label?.querySelector<HTMLElement>('.px-floating-contact__label-text');
	const list = el.querySelector<HTMLElement>('ul');
	if (!label || !text || !list) return;
	const desktop = window.matchMedia('(width > 768px)');

	const show = (link: HTMLElement) => {
		if (!desktop.matches) return;
		const first = !el.classList.contains('is-label');
		text.textContent = link.getAttribute('aria-label') ?? '';
		const box = link.getBoundingClientRect();
		const top = box.top - el.getBoundingClientRect().top + (box.height - label.offsetHeight) / 2;
		label.classList.toggle('is-jump', first);
		el.style.setProperty('--y', `${Math.round(top)}px`);
		el.style.setProperty('--w', `${text.offsetWidth}px`);
		if (first) void label.offsetWidth; // place it before it shows, so it pops in there
		label.classList.remove('is-jump');
		el.classList.add('is-label');
	};
	const hide = () => el.classList.remove('is-label');

	el.querySelectorAll<HTMLElement>('.px-floating-contact__link').forEach((link) => {
		link.addEventListener('pointerenter', (e) => e.pointerType === 'mouse' && show(link));
		link.addEventListener('focus', () => link.matches(':focus-visible') && show(link));
	});
	list.addEventListener('pointerleave', hide);
	list.addEventListener('focusout', (e) => !list.contains(e.relatedTarget as Node) && hide());
}

function initHubSpotChat(el: HTMLElement): void {
	const button = el.querySelector<HTMLButtonElement>('[data-px-hubspot-chat]');
	const src = button?.dataset.pxHubspotChat;
	if (!button || !src) return;

	// Loading: aria-busy on the button (the loading cursor, floating-contact.css) until HubSpot's widget is in, 20s at most
	let timeout = 0;
	const busy = (on: boolean) => {
		window.clearTimeout(timeout);
		button.toggleAttribute('aria-busy', on);
		if (on) timeout = window.setTimeout(() => busy(false), 20000);
	};
	let requested = false;
	const open = () => {
		const hs = window.HubSpotConversations;
		if (!hs) return;
		if (hs.widget.status().loaded) return hs.widget.open();
		busy(true);
		hs.widget.load({ widgetOpen: true });
	};
	button.addEventListener('click', () => {
		if (button.hasAttribute('aria-busy')) return;
		if (window.HubSpotConversations) return open();
		if (requested) return;
		requested = true;
		busy(true);
		// No chat bubble on page load: the widget loads when asked, open, and goes again once closed.
		window.hsConversationsSettings = { loadImmediately: false };
		(window.hsConversationsOnReady ??= []).push(() => {
			const hs = window.HubSpotConversations;
			hs?.on('widgetLoaded', () => busy(false));
			hs?.on('widgetClosed', () => hs.widget.remove());
			open();
		});
		const script = Object.assign(document.createElement('script'), { src, async: true, id: 'hs-script-loader' });
		script.addEventListener('error', () => {
			requested = false;
			busy(false);
		});
		document.body.append(script);
	});
}
